import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/primitives';
import { useMemo } from 'react';
import { NpmLinkRenderer } from './NpmLinkRenderer';
import { TextRenderer } from './TextRenderer';
const tooltipText = 'The current version has a difference with sharedDependency';
const DependencyType = {
    Dependency: 'dependency',
    Dev: 'devDependency',
    Optional: 'optionalDependency',
    Peer: 'peerDependency'
};
function convertToRowData(appVersion) {
    var _appVersion_overrides, _appVersion_dependencies, _appVersion_dependencies1, _appVersion_dependencies2, _appVersion_dependencies3;
    if (!appVersion) {
        return [];
    }
    const dependenciesNames = new Set();
    const getDependenciesHash = (dependencies, type)=>{
        return dependencies.filter((dependency)=>type ? dependency.type === type : true).reduce((memo, dependency)=>{
            dependenciesNames.add(dependency.name);
            let version = dependency.version;
            if (version) {
                const dependencyVersion = version.split('.');
                if (dependencyVersion.length === 2) {
                    version += '.0';
                }
                if (dependencyVersion.length === 1) {
                    version += '.0.0';
                }
            }
            memo[dependency.name] = version;
            return memo;
        }, {});
    };
    const sharedDependenciesHash = getDependenciesHash((_appVersion_overrides = appVersion.overrides) !== null && _appVersion_overrides !== void 0 ? _appVersion_overrides : []);
    const dependenciesHash = getDependenciesHash((_appVersion_dependencies = appVersion.dependencies) !== null && _appVersion_dependencies !== void 0 ? _appVersion_dependencies : [], DependencyType.Dependency);
    const devDependenciesHash = getDependenciesHash((_appVersion_dependencies1 = appVersion.dependencies) !== null && _appVersion_dependencies1 !== void 0 ? _appVersion_dependencies1 : [], DependencyType.Dev);
    const optionalDependenciesHash = getDependenciesHash((_appVersion_dependencies2 = appVersion.dependencies) !== null && _appVersion_dependencies2 !== void 0 ? _appVersion_dependencies2 : [], DependencyType.Optional);
    const peerDependenciesHash = getDependenciesHash((_appVersion_dependencies3 = appVersion.dependencies) !== null && _appVersion_dependencies3 !== void 0 ? _appVersion_dependencies3 : [], DependencyType.Peer);
    const compareVersions = (version1, version2)=>{
        return version1 && version2 ? version1 !== version2 : false;
    };
    const deps = Array.from(dependenciesNames);
    return deps.map((dependencyName)=>{
        const sharedDependencyVersion = sharedDependenciesHash[dependencyName];
        const dependencyVersion = dependenciesHash[dependencyName];
        const devDependencyVersion = devDependenciesHash[dependencyName];
        const optionalDependencyVersion = optionalDependenciesHash[dependencyName];
        const peerDependencyVersion = peerDependenciesHash[dependencyName];
        return {
            name: dependencyName,
            sharedDependency: {
                version: sharedDependencyVersion
            },
            dependency: {
                version: dependencyVersion,
                hasTooltip: compareVersions(dependencyVersion, sharedDependencyVersion)
            },
            devDependency: {
                version: devDependencyVersion,
                hasTooltip: compareVersions(devDependencyVersion, sharedDependencyVersion)
            },
            optionalDependency: {
                version: optionalDependencyVersion,
                hasTooltip: compareVersions(optionalDependencyVersion, sharedDependencyVersion)
            },
            peerDependency: {
                version: peerDependencyVersion,
                hasTooltip: compareVersions(peerDependencyVersion, sharedDependencyVersion)
            },
            tooltipText
        };
    });
}
export function RemoteDependencies(param) {
    let { appVersion, emptyStateText } = param;
    const rowData = useMemo(()=>convertToRowData(appVersion), [
        appVersion
    ]);
    if (!appVersion) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-center text-muted-foreground",
            children: "App version not found"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-col gap-4",
        children: rowData.length === 0 ? /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground",
            children: emptyStateText || /*#__PURE__*/ _jsx("p", {
                children: "No data found"
            })
        }) : /*#__PURE__*/ _jsxs(Table, {
            children: [
                /*#__PURE__*/ _jsx(TableHeader, {
                    children: /*#__PURE__*/ _jsxs(TableRow, {
                        children: [
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "Name"
                            }),
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "sharedDependency"
                            }),
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "dependency"
                            }),
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "devDependency"
                            }),
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "optionalDependency"
                            }),
                            /*#__PURE__*/ _jsx(TableHead, {
                                children: "peerDependency"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(TableBody, {
                    children: rowData.map((row, index)=>/*#__PURE__*/ _jsxs(TableRow, {
                            children: [
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(TextRenderer, {
                                        value: row.name,
                                        styleBold: true
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(NpmLinkRenderer, {
                                        value: {
                                            version: row.sharedDependency.version,
                                            hasTooltip: false
                                        },
                                        data: row
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(NpmLinkRenderer, {
                                        value: {
                                            version: row.dependency.version,
                                            hasTooltip: row.dependency.hasTooltip
                                        },
                                        data: row
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(NpmLinkRenderer, {
                                        value: {
                                            version: row.devDependency.version,
                                            hasTooltip: row.devDependency.hasTooltip
                                        },
                                        data: row
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(NpmLinkRenderer, {
                                        value: {
                                            version: row.optionalDependency.version,
                                            hasTooltip: row.optionalDependency.hasTooltip
                                        },
                                        data: row
                                    })
                                }),
                                /*#__PURE__*/ _jsx(TableCell, {
                                    children: /*#__PURE__*/ _jsx(NpmLinkRenderer, {
                                        value: {
                                            version: row.peerDependency.version,
                                            hasTooltip: row.peerDependency.hasTooltip
                                        },
                                        data: row
                                    })
                                })
                            ]
                        }, index))
                })
            ]
        })
    });
}
